import { MarkName, NodeName, markType, nodeType, schema } from "@wiki/core";
import type { MarkType, Node as ProseMirrorNode, NodeType, ResolvedPos } from "prosemirror-model";
import type { EditorState, Selection } from "prosemirror-state";

export { schema };

/**
 * Resolved node and mark types. `schema.nodes.x` is `NodeType | undefined`
 * under `noUncheckedIndexedAccess`; resolving every type once here keeps the
 * rest of the editor free of non-null assertions.
 */
export const nodes = {
  doc: nodeType(NodeName.doc),
  paragraph: nodeType(NodeName.paragraph),
  heading: nodeType(NodeName.heading),
  blockquote: nodeType(NodeName.blockquote),
  bulletList: nodeType(NodeName.bulletList),
  orderedList: nodeType(NodeName.orderedList),
  listItem: nodeType(NodeName.listItem),
  checkboxList: nodeType(NodeName.checkboxList),
  checkboxItem: nodeType(NodeName.checkboxItem),
  codeBlock: nodeType(NodeName.codeBlock),
  hr: nodeType(NodeName.hr),
  table: nodeType(NodeName.table),
  tableRow: nodeType(NodeName.tableRow),
  tableCell: nodeType(NodeName.tableCell),
  tableHeader: nodeType(NodeName.tableHeader),
  image: nodeType(NodeName.image),
  video: nodeType(NodeName.video),
  attachment: nodeType(NodeName.attachment),
  embed: nodeType(NodeName.embed),
  notice: nodeType(NodeName.containerNotice),
  toggle: nodeType(NodeName.containerToggle),
  mathInline: nodeType(NodeName.mathInline),
  mathBlock: nodeType(NodeName.mathBlock),
  emoji: nodeType(NodeName.emoji),
  mention: nodeType(NodeName.mention),
  br: nodeType(NodeName.br),
  text: nodeType(NodeName.text),
} satisfies Record<string, NodeType>;

export const marks = {
  link: markType(MarkName.link),
  strong: markType(MarkName.strong),
  em: markType(MarkName.em),
  underline: markType(MarkName.underline),
  strikethrough: markType(MarkName.strikethrough),
  code: markType(MarkName.codeInline),
  highlight: markType(MarkName.highlight),
  placeholder: markType(MarkName.placeholder),
  comment: markType(MarkName.comment),
} satisfies Record<string, MarkType>;

export interface FoundNode {
  node: ProseMirrorNode;
  /** The position *before* the node. */
  pos: number;
  depth: number;
}

/** The innermost ancestor of the selection head matching `predicate`. */
export function findParent(
  $pos: ResolvedPos,
  predicate: (node: ProseMirrorNode) => boolean,
): FoundNode | null {
  for (let depth = $pos.depth; depth > 0; depth -= 1) {
    const node = $pos.node(depth);
    if (predicate(node)) return { node, pos: $pos.before(depth), depth };
  }
  return null;
}

export function findParentOfType($pos: ResolvedPos, type: NodeType): FoundNode | null {
  return findParent($pos, (node) => node.type === type);
}

export function findParentOfAnyType(
  $pos: ResolvedPos,
  types: readonly NodeType[],
): FoundNode | null {
  return findParent($pos, (node) => types.includes(node.type));
}

/** The top-level block containing a position, or `null` at doc level. */
export function findTopLevelBlock($pos: ResolvedPos): FoundNode | null {
  if ($pos.depth === 0) return null;
  const node = $pos.node(1);
  return { node, pos: $pos.before(1), depth: 1 };
}

export function isInNodeOfType(state: EditorState, type: NodeType): boolean {
  return findParentOfType(state.selection.$from, type) !== null;
}

/** True when the caret sits in a code block or under an inline code mark. */
export function isInCode(state: EditorState): boolean {
  const { $from, empty } = state.selection;
  if (findParentOfType($from, nodes.codeBlock) !== null) return true;
  if (findParentOfType($from, nodes.mathBlock) !== null) return true;
  if (findParentOfType($from, nodes.mathInline) !== null) return true;
  if (empty) return marks.code.isInSet(state.storedMarks ?? $from.marks()) !== undefined;
  return state.doc.rangeHasMark(state.selection.from, state.selection.to, marks.code);
}

/** The node the selection points at, when it is a node selection. */
export function selectedNode(selection: Selection): ProseMirrorNode | null {
  const node = (selection as { node?: ProseMirrorNode }).node;
  return node ?? null;
}

export function isListType(type: NodeType): boolean {
  return type === nodes.bulletList || type === nodes.orderedList || type === nodes.checkboxList;
}

export function itemTypeFor(listType: NodeType): NodeType {
  return listType === nodes.checkboxList ? nodes.checkboxItem : nodes.listItem;
}

export function listTypeFor(itemType: NodeType): NodeType {
  return itemType === nodes.checkboxItem ? nodes.checkboxList : nodes.bulletList;
}
